import React, {useEffect, useState} from "react";
import {Box, Button, Typography} from '@mui/material';
import {bindActionCreators} from "redux";
import {connect, useSelector} from 'react-redux';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import {CONTENT_ITEM_TYPE, TaskContentItem, Test, Question, TEST_STATE, TestSubmition} from "../../../common/course";
import courseActions from '../../../store/course/actions';
import {getYTvideoLinkFromUrl} from "../../../utils";
import MultiFormatImage from '../multiFormatImage';

import {CommonState} from "../../../store";
import {selectTestById, selectTestSubmitionById} from "../../../store/course/selectors";

import './style.scss';
import StartTest from './test/StartTest';
import QuestionComponent from './test/Question';
import SuccessComponent from './test/Success';
import FailComponent from './test/Fail';

type Props = {
    content: TaskContentItem;
    fetchTest: Function;
    changeTestStatus: Function;
    sendTestResults: Function,
    fetchTestLastSubmition: Function,
}

type TestComponentProps = {
    test: Test,
    testStatus: TEST_STATE,
    setTestStatus: Function,
    answers: Question['options'],
    setAnswers: Function,
    testSubmition?: TestSubmition,
    loadTest: Function
};

const contentParsers: Record<CONTENT_ITEM_TYPE, Function> = {
    [CONTENT_ITEM_TYPE.MARKUP]: (content: TaskContentItem) => (
        <Box dangerouslySetInnerHTML={{__html: String(content.content)}} id="agc"/>
    ),
    [CONTENT_ITEM_TYPE.VIDEO]: (content: TaskContentItem) => (
        <Box sx={{position: 'relative', paddingBottom: '56.25%'}} id="agc">
            <iframe
                id="player"
                //@ts-expect-error
                type="text/html"
                src={getYTvideoLinkFromUrl(String(content.url))}
                frameBorder="0"
                className="video-frame"
            />
        </Box>
    ),
    [CONTENT_ITEM_TYPE.IMAGE]: (content: TaskContentItem) => (
        content.image
        ? (<Box id="agc">
            <MultiFormatImage image={content.image}></MultiFormatImage>
        </Box>)
        : null
    ),
    [CONTENT_ITEM_TYPE.TEST]: (
        content: TaskContentItem,
        {test, setTestStatus, answers, setAnswers, testSubmition, loadTest}: TestComponentProps
    ) => {
        const testStatus = testSubmition?.state;
        if (testStatus === TEST_STATE.FAIL && testSubmition) {
            return <FailComponent testSubmition={testSubmition} setTestStatus={setTestStatus}/>;
        }
        if (testStatus === TEST_STATE.NOT_STARTED) {
            return <StartTest onBtnClick={() => {
                loadTest()
                setTestStatus(TEST_STATE.IN_PROGRESS)
            }}/>
        }
        if (testStatus === TEST_STATE.CHECKING) {
            return (
                <Typography
                    component='h3'
                    sx={{
                    textAlign: 'center',
                    fontSize: '3rem',
                    my: 5,
                    }}
                >
                    Проверка...
                </Typography>
            )
        }
        if ((testStatus === TEST_STATE.IN_PROGRESS) && test?.questions?.length) {
            const currentQuestion = test.questions[answers.length];
            if (!currentQuestion) {
                return null;
            }
            //@ts-ignore
            return (
                <QuestionComponent
                    question={currentQuestion}
                    answers={answers}
                    total={test.questions.length}
                    currentIdx={answers.length}
                    //@ts-expect-error
                    onSubmitAnswer={answer => setAnswers([...answers, answer])}
                />
            );
        }
        if (testStatus === TEST_STATE.SUCCESS) {
            return <SuccessComponent />;
        }
        
    },
    [CONTENT_ITEM_TYPE.FILE]: (content: TaskContentItem) => {
        const FILE_EXT = {
            pdf:  PictureAsPdfIcon,
            defaultVal:  FilePresentIcon,
        }
        const getFileIcon = (ext: string) => {
            //@ts-expect-error
            const Icon =  FILE_EXT[ext] || FILE_EXT.defaultVal;
            return <Icon sx={{mr: 1}}/>
        }
        return (
            <Box sx={{display: 'inline-flex', justifyContent: 'center', mr: 1}}>
                <Button
                    target="_blank"
                    href={content.url}
                    component={'a'}
                    variant="contained"
                    sx={{
                        display: 'inline-flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textDecoration: 'none',
                        margin: '0 auto',
                    }}
                >
                    {getFileIcon(String(content.fileType))}
                    {content.fileName}
                </Button>
            </Box>

        )
    }
};

const View = ({content, fetchTest, changeTestStatus, sendTestResults, fetchTestLastSubmition}: Props) => {
    const parseContent = contentParsers[content.type];
    const testSubmition = useSelector((state: CommonState) => selectTestSubmitionById(state, content.testId ?  String(content.testId) : undefined));
    const test = useSelector((state: CommonState) => selectTestById(state, content.testId ?  String(content.testId) : undefined));
    const setTestStatus = (status: TEST_STATE) => changeTestStatus({id: content.testId, state: status});
    const [answers, setAnswers] = useState<{id: string, values: Question['options']}[]>([]);
    useEffect(() => {
        if (content.testId && content.testId !== testSubmition?.id) {
            fetchTestLastSubmition(content.testId); 
        }
    }, [content]);

    const loadTest = () => {
        fetchTest(content.testId);
    }
    useEffect(() => {
        if (!answers.length) {
            return;
        }
        if (answers.length === test?.questions.length) {
            sendTestResults({testId: test.id, answers});
            setTestStatus(TEST_STATE.CHECKING);
        }
    }, [answers]);

    if (!parseContent) {
        console.error('Unexpected type of content')
        return null;
    }
    return parseContent(
        content,
        {
           test,
           setTestStatus,
           setAnswers,
           answers,
           testSubmition,
           loadTest,
        }
    )
};

//@ts-ignore
const mapStateToProps = (state) => { 
    return {}
};

//@ts-expect-error
const dispatchProps = (dispatch) => ({
    sendTestResults: bindActionCreators(courseActions.sendTestResults, dispatch),
    fetchTest: bindActionCreators(courseActions.fetchTest, dispatch),
    fetchTestLastSubmition: bindActionCreators(courseActions.getTestLastSubmition, dispatch),
    changeTestStatus: bindActionCreators(courseActions.setTestStatus, dispatch),
});

export default connect(mapStateToProps, dispatchProps)(View);